export interface TicketAllocationSettings {
  ticket_base_amount: number;
  ticket_additional_amount: number;
  ticket_max_amount: number;
  wristband_per_category: number;
  wristband_max_amount: number;
  tool_car_max_amount: number;
  tickets_enabled: boolean;
}

export const defaultSettings: TicketAllocationSettings = {
  ticket_base_amount: 2,
  ticket_additional_amount: 1,
  ticket_max_amount: 5,
  wristband_per_category: 3,
  wristband_max_amount: 10,
  tool_car_max_amount: 2,
  tickets_enabled: false
};

export async function getTicketAllocationSettings(eventId?: string): Promise<TicketAllocationSettings> {
  try {
    // If eventId is provided, fetch event-specific settings
    if (eventId) {
      const { createClient } = await import('@supabase/supabase-js');
      const supabase = createClient(
        import.meta.env.VITE_SUPABASE_URL,
        import.meta.env.VITE_SUPABASE_ANON_KEY
      );
      
      // First check if tickets are enabled for this event
      const { data: ticketEnabledData, error: ticketEnabledError } = await supabase
        .from('event_settings')
        .select('setting_value')
        .eq('event_id', eventId)
        .eq('setting_key', 'tickets_enabled')
        .maybeSingle();
        
      // If tickets are explicitly disabled, return settings with tickets_enabled = false
      if (!ticketEnabledError && ticketEnabledData && ticketEnabledData.setting_value === 'false') {
        return { ...defaultSettings, tickets_enabled: false };
      }
      
      // Fetch all event-specific settings
      const { data: eventSettings, error: eventError } = await supabase
        .from('event_settings')
        .select('setting_key, setting_value')
        .eq('event_id', eventId)
        .in('setting_key', [
          'ticket_base_amount',
          'ticket_additional_amount',
          'ticket_max_amount',
          'wristband_per_category',
          'wristband_max_amount',
          'tool_car_max_amount',
          'tickets_enabled'
        ]);

      if (!eventError && eventSettings && eventSettings.length > 0) {
        // Convert event settings to the expected format
        const settings = { ...defaultSettings };
        
        eventSettings.forEach(setting => {
          if (setting.setting_key === 'tickets_enabled') {
            settings[setting.setting_key] = setting.setting_value === 'true';
          } else {
            settings[setting.setting_key as keyof TicketAllocationSettings] = 
              parseInt(setting.setting_value) || 0;
          }
        });
        
        return settings;
      }
    }
    
    // If no event-specific settings or no eventId, fetch global settings
    const { createClient } = await import('@supabase/supabase-js');
    const supabase = createClient(
      import.meta.env.VITE_SUPABASE_URL,
      import.meta.env.VITE_SUPABASE_ANON_KEY
    );
    
    const { data: globalSettings, error: globalError } = await supabase
      .from('system_settings')
      .select('setting_key, setting_value')
      .in('setting_key', [
        'ticket_base_amount',
        'ticket_additional_amount',
        'ticket_max_amount',
        'wristband_per_category',
        'wristband_max_amount',
        'tool_car_max_amount',
        'tickets_enabled'
      ]);
      
    if (!globalError && globalSettings && globalSettings.length > 0) {
      // Convert global settings to the expected format
      const settings = { ...defaultSettings };
      
      globalSettings.forEach(setting => {
        if (setting.setting_key === 'tickets_enabled') {
          settings[setting.setting_key] = setting.setting_value === 'true';
        } else {
          settings[setting.setting_key as keyof TicketAllocationSettings] = 
            parseInt(setting.setting_value) || 0;
        }
      });
      
      return settings;
    }
    
    // Return default values if fetch fails
    return { ...defaultSettings };
  } catch (error) {
    console.error('Error fetching ticket allocation settings:', error);
    // Return default values if fetch fails
    return { ...defaultSettings };
  }
}

export function calculateTicketAllocationLogic(
  categories: any[],
  existingRegistrations: any[] = [],
  includeToolCar: boolean = false,
  toolCarCount: number = 1,
  settings: TicketAllocationSettings = defaultSettings
) {
  // If tickets are disabled, return zeros for all ticket/wristband counts
  if (!settings.tickets_enabled) {
    return {
      totalTickets: 0,
      totalWristbands: 0,
      toolCarCount: 0,
      includeToolCar: false,
      categories: categories.map(category => ({
        ...category,
        tickets_included: 0,
        wristbands_included: 0
      }))
    };
  }

  // Combine existing registrations with new categories
  const allCategories = [...existingRegistrations, ...categories];
  
  // Calculate tickets and wristbands
  let totalTickets = 0;
  let totalWristbands = 0;
  
  // Calculate tool car count (limited by max amount)
  const actualToolCarCount = includeToolCar 
    ? Math.min(toolCarCount, settings.tool_car_max_amount) 
    : 0;
  
  const categoriesWithAllocation = allCategories.map((category, index) => {
    // For the first category, use the base tickets
    const isFirstCategory = index === 0;
    
    // Calculate tickets
    const categoryTickets = isFirstCategory 
      ? settings.ticket_base_amount 
      : settings.ticket_additional_amount;
    
    // Add to total, respecting the maximum
    const newTotalTickets = Math.min(totalTickets + categoryTickets, settings.ticket_max_amount);
    // The actual tickets this category contributes (may be less than categoryTickets if we hit the max)
    const actualCategoryTickets = newTotalTickets - totalTickets;
    totalTickets = newTotalTickets;
    
    // Calculate wristbands
    const categoryWristbands = settings.wristband_per_category;
    
    // Add to total, respecting the maximum
    const newTotalWristbands = Math.min(totalWristbands + categoryWristbands, settings.wristband_max_amount);
    // The actual wristbands this category contributes
    const actualCategoryWristbands = newTotalWristbands - totalWristbands;
    totalWristbands = newTotalWristbands;
    
    return {
      ...category,
      tickets_included: actualCategoryTickets,
      wristbands_included: actualCategoryWristbands
    };
  });
  
  return {
    totalTickets,
    totalWristbands,
    toolCarCount: actualToolCarCount,
    includeToolCar,
    categories: categoriesWithAllocation
  };
}